import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PurchaseCountTable from "./PurchaseCountTable";
import PurchaseVolTable from "./PurchaseVolTable";
import TopVendor from "./TopVendor";
import TopItem from "./TopItem";
import { Bar, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { formatSalesValue } from './../../utils/formatUtils';
import OutletSearch from "../SelectSearch/OutletSearch";
import "../../scss/FoodBeverage.scss";
import InventoryPurchaseTables from "../Inventory/InventoryPurchaseTables";

const InventoryByOutlet = ({ viewMode }) => {
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [inputValue, setInputValue] = useState("");
    const [purchaseRequisitions, setPurchaseRequisitions] = useState([]);
    const [purchaseItems, setPurchaseItems] = useState([]);
    const [topItem, setTopItem] = useState([]);
    const [topVendor, setTopVendor] = useState([]);
    const [purchaseCountTable, setPurchaseCountTable] = useState([]);
    const [purchaseVolTable, setPurchaseVolTable] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [vendorChart, setVendorChart] = useState([]);
    const [token, setToken] = useState("");
    const [tableHeight, setTableHeight] = useState(null);
    const monthlyTableRef = useRef(null);
    const effectRan = useRef(false);

    const [searchType, setSearchType] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [searchPurchases, setSearchPurchases] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState("");

    let navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL

    const formatDateToYMD = (date) => {
        if (!(date instanceof Date) || isNaN(date)) {
            throw new Error("Invalid date provided");
        }
        return date.toLocaleDateString('en-CA');
    }

    useEffect(() => {
        const storedToken = localStorage.getItem("token");

        if (!storedToken) {
            navigate("/");
        } else {
            setToken(storedToken);
        }
    }, [navigate, token]);

    useEffect(() => {
        if (effectRan.current) return;

        const storedStart = localStorage.getItem("startDate");
        const storedEnd = localStorage.getItem("endDate");

        if (storedStart) setStartDate(new Date(JSON.parse(storedStart)));
        if (storedEnd) setEndDate(new Date(JSON.parse(storedEnd)));

        effectRan.current = true;

        localStorage.removeItem("startDate");
        localStorage.removeItem("endDate");
    }, []);

    useEffect(() => {
        if (!selectedOutlet || !token) return

        if (!startDate || !endDate) return

        fetchData();
        fetchSVPData();
    }, [selectedOutlet, token]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const fetchData = async () => {
        const formattedStartDate = formatDateToYMD(startDate)
        const formattedEndDate = formatDateToYMD(endDate)
        const url = `${baseUrl}/reqfilterfirst/?secondtime=${formattedEndDate}&firsttime=${formattedStartDate}&outlet_name=${encodeURIComponent(selectedOutlet)}`;
        try {
            const response = await axios.get(url);
            setPurchaseItems(response.data.purchase_items_data || []);
            setPurchaseRequisitions(response.data.purchaserequisition || []);
            setVendorChart(response.data.vendorwise_totals);
        } catch (err) {
            console.error("Error fetching purchase requisitions:", err);
        }
    }

    const fetchSVPData = async () => {
        try {
            const response = await axios.post(
                `${baseUrl}/salesvspurchase`,
                {
                    token: "test",
                    outlet: selectedOutlet
                }
            );
            setTopItem(response.data.toppurchasingvendors_bycount);
            setTopVendor(response.data.toppurchasingvendors_byvolume);
            setPurchaseCountTable(response.data.purchaseitembycount_responseJson);
            setPurchaseVolTable(response.data.purchaseitembyvolume_responseJson);

            setPurchaseData(response.data.monthlyPurchases);
            setSalesData(response.data.monthlySales);
        } catch (err) {
            console.error("Error fetching sale vs purchase data:", err);
        }
    };

    const handleSearch = () => {
        fetchData();
        fetchSVPData();
        setSearchType("");
        setSearchValue("");
    };

    const handleInputSearch = async () => {
        setSearchError("");

        if (!searchType) {
            setSearchError("Please select GRN or Bill Number");
            return;
        }

        if (!searchValue) {
            setSearchError("Please enter a value to search");
            return;
        }

        setSearchLoading(true);

        try {
            const payload = {
                outlet_name: selectedOutlet || "",
                token: token,
                ...(searchType === "grn" && { grn: Number(searchValue) }),
                ...(searchType === "bill_number" && { bill_number: Number(searchValue) }),
            };

            const res = await axios.post(
                `${baseUrl}/getOrderedPurchases`,
                payload
            );

            setSearchPurchases(res.data.purchases || []);
        } catch (err) {
            setSearchError(
                err?.response?.data?.message || "Search failed. Please try again."
            );
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        if (!searchType || !searchValue) return;

        const timeout = setTimeout(() => {
            handleInputSearch();
        }, 500);

        return () => clearTimeout(timeout);
    }, [searchValue, searchType]);

    const lineData = {
        labels: purchaseData && purchaseData.length > 0 ? purchaseData.map((item) => item.monthName) : [], // Check if data exists
        datasets: [
            {
                label: "Sales vs Time Chart",
                backgroundColor: "rgb(255, 99, 132)",
                borderColor: "#94a3b8",
                data: salesData && salesData.length > 0 ? salesData.map((item) => parseFloat(item.Total)) : [], // Check if data exists
            },
            {
                label: "Purchase vs Time Chart",
                backgroundColor: "rgb(54, 162, 235)",
                borderColor: "#4b6cb7",
                data: purchaseData && purchaseData.length > 0 ? purchaseData.map((item) => parseFloat(item.Total)) : [], // Check if data exists
            },
        ],
    };

    const barData = {
        labels: vendorChart && vendorChart.length > 0 ? vendorChart.map((item) => item.vendor) : [], // Check if data exists
        datasets: [
            {
                label: "Sales vs Vendor",
                backgroundColor: "rgb(255, 99, 132)",
                borderColor: "#94a3b8",
                data: vendorChart && vendorChart.length > 0 ? vendorChart.map((item) => parseFloat(item.total)) : [], // Check if data exists
            },
        ],
    };

    const handleChange = (element) => {
        const {
            IDIntbl_PurchaseRequisition,
            Date,
            Company_Name,
            Outlet_Name,
            State,
            purchaseBillNumber,
            DiscountAmount,
            TaxAmount,
            TotalAmount,
            GRN,
            purchase_from
        } = element;

        navigate("/detail", {
            state: {
                ID: IDIntbl_PurchaseRequisition,
                date: Date,
                companyName: Company_Name,
                outletName: Outlet_Name,
                status: State,
                billNum: purchaseBillNumber,
                discount: DiscountAmount,
                tax: TaxAmount,
                total: TotalAmount,
                GRN: GRN,
                startDate,
                endDate,
                viewMode,
                purchase_from
            }
        });
    };

    // const navigateWithViewMode = (path) => {
    //     navigate(path);
    // };

    useEffect(() => {
        if (monthlyTableRef.current) {
            setTableHeight(monthlyTableRef.current.clientHeight);
        }
    }, [purchaseData, salesData]);

    return (
        <>
            <div className="flex justify-center w-full px-10 pt-4">
                {/* <div className="flex justify-between w-full px-10"> */}
                <OutletSearch
                    selectedOutlet={selectedOutlet}
                    setSelectedOutlet={setSelectedOutlet}
                    token={token}
                />

                {/* Buttons Section */}
                {/* <div className="flex gap-4">
                    <button
                        className="w-28 py-1 text-xs border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg"
                        onClick={() => navigateWithViewMode("/detailedpurchasereport")}
                    >
                        Detailed Purchase Report
                    </button>

                    <button
                        className="w-28 py-1 text-xs border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg"
                        onClick={() => navigateWithViewMode("/statement")}
                    >
                        View Statement
                    </button>

                    <button
                        className="w-28 py-1 text-xs border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg"
                        onClick={() => navigateWithViewMode("/costcenter")}
                    >
                        Cost Center
                    </button>

                    <button
                        className="w-28 py-1 text-xs border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg"
                        onClick={() => navigateWithViewMode("/items")}
                    >
                        Item History
                    </button>
                </div> */}
            </div>

            <div className="py-3 px-10">
                <div className="w-full border border-slate-300 rounded-xl bg-white p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-md mb-3">

                    {/* LEFT — DATE PICKERS */}
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="flex flex-col items-center">
                            <label className="font-semibold text-sm md:text-base mb-1 text-center">
                                Date Selection
                            </label>

                            <div className="flex gap-3 z-20">
                                {/* FROM */}
                                <div className="flex flex-col">
                                    <label className="text-xs md:text-sm font-medium">From</label>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-28 px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
                                        popperPlacement="bottom"
                                        popperModifiers={[
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 8],
                                                },
                                            },
                                        ]}
                                    />
                                </div>

                                {/* TO */}
                                <div className="flex flex-col">
                                    <label className="text-xs md:text-sm font-medium">To</label>
                                    <DatePicker
                                        selected={endDate}
                                        onChange={(date) => setEndDate(date)}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-28 px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
                                        popperPlacement="bottom"
                                        popperModifiers={[
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, 8],
                                                },
                                            },
                                        ]}
                                    />
                                </div>

                            </div>
                        </div>

                        {/* SEARCH BUTTON */}
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-400 bg-slate-700 text-white hover:bg-slate-100 hover:!text-slate-700 transition">
                            Search
                        </button>
                    </div>


                    {/* CENTER — DATE DISPLAY */}
                    <div className="flex flex-col items-center text-center">
                        <div className="flex w-fit border border-gray-300 rounded-md overflow-hidden mb-2.5">
                            {[
                                { label: "GRN", value: "grn" },
                                { label: "Bill Number", value: "bill_number" }
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        setSearchType(opt.value);
                                    }}
                                    className={`flex-1 min-w-[140px] px-4 py-2 text-sm text-center transition-all
                                        ${searchType === opt.value
                                            ? "bg-blue-600 text-white font-medium"
                                            : "bg-white text-gray-700 hover:bg-gray-50"
                                        }
                                    `}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        <input
                            type="number"
                            placeholder={`Enter ${searchType === "grn" ? "GRN" : "Bill Number"}`}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="border px-3 py-1.5 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-slate-400"
                        />
                    </div>


                    {/* RIGHT — FILTER + REPORT BUTTON */}
                    <div className="flex flex-col sm:flex-row items-center gap-4">

                        <div className="flex items-center gap-2">
                            <label className="font-medium text-sm">Search:</label>

                            <input
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder="Vendor"
                                className="h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:italic"
                            />
                        </div>

                        <button
                            onClick={fetchData}
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-400 bg-slate-700 text-white hover:bg-slate-100 hover:!text-slate-700 transition">
                            View Reports
                        </button>

                    </div>
                </div>
            </div>

            <InventoryPurchaseTables
                purchaseRequisitions={purchaseRequisitions}
                purchaseItems={purchaseItems}
                handleChange={handleChange}
                formatSalesValue={formatSalesValue}
                searchType={searchType}
                searchValue={searchValue}
                searchLoading={searchLoading}
                searchError={searchError}
                searchPurchases={searchPurchases}
            />

            <div className="flex justify-evenly my-12">
                <div className="flex flex-col">
                    <div className="flex justify-center font-serif">Top Item by Volume</div>
                    <PurchaseVolTable PurchaseVolTableinfo={purchaseVolTable} />
                </div>
                <div className="flex flex-col">
                    <div className="flex justify-center font-serif">Top Item by Count</div>
                    <PurchaseCountTable PurchaseCountTableinfo={purchaseCountTable} />
                </div>
                <div className="flex flex-col">
                    <div className="flex justify-center font-serif">Top Vendor by Volume</div>
                    <TopVendor TopVendorinfo={topVendor} />
                </div>
                <div className="flex flex-col">
                    <div className="flex justify-center font-serif">Top Vendor by Count</div>
                    <TopItem TopIteminfo={topItem} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-8">
                {/* Bar Chart */}
                <div className="p-6 bg-white shadow-xl rounded-2xl">
                    <h2 className="text-center font-semibold 2xl:text-xl xl:text-lg text-gray-800 mb-4">
                        Sales by Vendor
                    </h2>
                    <div className="h-[450px]">
                        <Bar data={barData} options={{ maintainAspectRatio: false }} />
                    </div>
                </div>

                {/* Line Chart */}
                <div className="p-6 bg-white shadow-xl rounded-2xl">
                    <h2 className="text-center font-semibold 2xl:text-xl xl:text-lg text-gray-800 mb-4">
                        Sales vs Purchase Over Time
                    </h2>
                    <div className="h-[450px]">
                        <Line data={lineData} options={{ maintainAspectRatio: false }} />
                    </div>
                </div>
            </div>

            <div className="flex justify-center w-[90%] mx-auto mb-10 gap-10 mt-20">
                {/* Vendor Sales Table (Scrolls if Data Exceeds Monthly Table Height) */}
                <div className="w-1/2">
                    <div
                        className="border border-gray-300 rounded-2xl overflow-y-auto tiny-scrollbar"
                        style={{ maxHeight: tableHeight ? `${tableHeight}px` : "auto" }}
                    >
                        <table className="w-full text-center border-collapse 2xl:text-base xl:text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr className="border-b-2 border-gray-400">
                                    <th className="p-2">Vendor</th>
                                    <th className="p-2">Sales</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendorChart && vendorChart.length > 0 ? (
                                    vendorChart.map((item, index) => (
                                        <tr key={index} className="border-b border-gray-200">
                                            <td className="p-2">{item.vendor}</td>
                                            <td className="p-2">{formatSalesValue(item.total)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="p-4">Data not available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="w-1/2 flex" ref={monthlyTableRef}>
                    {/* Monthly Purchase Table */}
                    <div className="w-1/2 px-2">
                        <div className="border border-gray-300 rounded-2xl">
                            <table className="w-full text-center border-collapse 2xl:text-base xl:text-sm">
                                <thead className="bg-slate-50">
                                    <tr className="border-b-2 border-gray-400">
                                        <th className="p-2">Month</th>
                                        <th className="p-2">Purchase</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchaseData.length > 0 ? (
                                        purchaseData.map((item, index) => (
                                            <tr key={index} className="border-b border-gray-200">
                                                <td className="p-2">{item.monthName}</td>
                                                <td className="p-2">{formatSalesValue(item.Total)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="p-4">No data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Monthly Sales Table */}
                    <div className="w-1/2 px-2">
                        <div className="border border-gray-300 rounded-2xl">
                            <table className="w-full text-center border-collapse 2xl:text-base xl:text-sm">
                                <thead className="bg-slate-50">
                                    <tr className="border-b-2 border-gray-400">
                                        <th className="p-2">Month</th>
                                        <th className="p-2">Sales</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salesData.length > 0 ? (
                                        salesData.map((item, index) => (
                                            <tr key={index} className="border-b border-gray-200">
                                                <td className="p-2">{item.monthName}</td>
                                                <td className="p-2">{formatSalesValue(item.Total)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="p-4">No data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default InventoryByOutlet;
